import {
  DataType,
  type SettingTypeMetadata,
  type SettingTypeValueMap,
} from '@wix/editor-platform-ooi-metadata-resolver';
import { type ISettingsParam, SettingsParamType } from '@wix/tpa-settings';

export const getMetadata = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

export const settingsParam = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(type, defaultValue),
});

export const enumSettingsParam = <T extends string>(
  defaultValue: T | ((args: { isMobile: boolean }) => T),
) => ({
  getDefaultValue: (args: any) =>
    typeof defaultValue === 'function' ? defaultValue(args) : defaultValue,
  metadata: getMetadata(DataType.textEnum, defaultValue as T),
});

export const typedSettingsParam = <
  T extends DataType,
  Type extends SettingsParamType,
>(
  dataType: T,
  type: Type | undefined,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  type,
  ...settingsParam(dataType, defaultValue),
});

export enum AccessibilityHtmlTags {
  MainHeading = 'h1',
  SecondaryHeading = 'h2',
  ThirdHeading = 'h3',
  Paragraph = 'p',
}

export const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});
